import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_YOUTUBE,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps, getFieldValue } from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
  getProfileUrlIdentifier,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { apiBaseUrl } from '../../util/api';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H4,
  Page,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSingleColumn,
  NamedRedirect,
  ExternalLink,
  ResponsiveImage,
  VocaTalesExpertBadge,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from '../ListingPage/SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionYoutubeVideoMaybe from './SectionYoutubeVideoMaybe';
import SectionSocialMediaMaybe from './SectionSocialMediaMaybe';
import SectionWebsiteMaybe from './SectionWebsiteMaybe';
import SectionPastWorks from './SectionPastWorks';
import { MapPin, ChevronDown, Star } from 'lucide-react';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

// Role eyebrow shown in the dossier header, keyed by category.
// Same labels as the roster (ExpertsPage) for continuity.
const ROLE_LABELS = {
  'written-story': 'Written Story Master',
  'motion-story': 'Motion Story Master',
  'visual-story': 'Visual Story Master',
  'business-story': 'Business Story Master',
  'content-master': 'Content Master',
  publishing: 'Publishing Master',
  'education-coaching': 'Coaching Master',
};

const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$' };

// Avatar fallback gradients — same family as the marketing site and the roster.
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#EF2D56,#F5A623)',
  'linear-gradient(135deg,#6C4CF1,#EF2D56)',
  'linear-gradient(135deg,#F5A623,#12A87B)',
  'linear-gradient(135deg,#12A87B,#6C4CF1)',
  'linear-gradient(135deg,#EF2D56,#6C4CF1)',
  'linear-gradient(135deg,#F5A623,#EF2D56)',
];

const gradientFor = name => {
  const hash = (name || '').split('').reduce((a, b) => {
    a = (a << 5) - a + b.charCodeAt(0);
    return a & a;
  }, 0);
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const getInitials = name =>
  (name || '')
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

/**
 * Helper function to extract profile image variants for social sharing
 * Profile images use square variants. Sharetribe only generates square-small (120x120)
 * and square-small2x (240x240) for profile images.
 * We use the largest available variant even though it's smaller than ideal for social media.
 * @param {Object} user - User object with profileImage
 * @returns {Object} Object with facebookImages and twitterImages arrays
 */
const getProfileImageVariants = user => {
  const profileImage = user?.profileImage;
  if (!profileImage?.attributes?.variants) {
    return { facebookImages: null, twitterImages: null };
  }

  const variants = profileImage.attributes.variants;

  // Sharetribe only provides square-small2x (240x240) and square-small (120x120)
  // for profile images. Use the largest available.
  const variant = variants['square-small2x'] || variants['square-small'];

  if (!variant) {
    return { facebookImages: null, twitterImages: null };
  }

  // Create image object in the format expected by Page component
  const imageObj = {
    url: variant.url,
    width: variant.width,
    height: variant.height,
  };

  // Use same image for both Facebook and Twitter
  return {
    facebookImages: [imageObj],
    twitterImages: [imageObj],
  };
};

// The editor's red pen — drawn under the Master's name, the same stroke
// family as the marketing site's dossier popup.
const PenUnderline = () => (
  <svg className={css.pen} viewBox="0 0 200 12" preserveAspectRatio="none" aria-hidden="true">
    <path d="M3,9 C48,3 128,10 197,5" />
  </svg>
);

/**
 * The dossier header — the marketing site's Master dossier popup, unfolded
 * into a full page head. Portrait panel left, reading side right: role
 * eyebrow, name under the red pen, headline, and the booking path.
 */
export const DossierHeader = props => {
  const {
    user,
    displayName,
    metadata,
    listingsCount,
    reviewsCount,
    showLinkToProfileSettingsPage,
    isAdmin,
    isCurrentUser,
  } = props;

  const publicData = user?.attributes?.profile?.publicData || {};
  const privateData = user?.attributes?.profile?.privateData || {};
  const userType = publicData?.userType;
  const category = publicData?.category || metadata?.category;
  const pendingCategory = privateData?.pendingCategory;
  const categoryApproved = publicData?.categoryApproved;
  const categoryStatus = publicData?.categoryStatus;
  const headline = publicData?.headline;
  const location = publicData?.location;
  const price = publicData?.startingAtPrice;
  const currency = publicData?.startingAtPriceCurrency || 'USD';

  const isExpert = userType === 'expert';
  const isMaster = metadata?.isMaster === true;
  const isTopAdvisor = metadata?.badges?.includes('top-advisor') || false;
  const showApprovedRole =
    isExpert && category && (categoryApproved === true || categoryApproved === undefined);
  const showPendingRole =
    isExpert && !showApprovedRole && (categoryStatus === 'pending' || pendingCategory);

  const profileImage = user?.profileImage;

  return (
    <header className={css.dossier}>
      <div className={css.dossierWrap}>
        <div className={css.dossierGrid}>
          <div
            className={css.portrait}
            style={profileImage ? null : { background: gradientFor(displayName) }}
          >
            {profileImage ? (
              <ResponsiveImage
                rootClassName={css.portraitImage}
                alt={displayName || ''}
                image={profileImage}
                variants={['square-small2x', 'square-small']}
                sizes="(max-width: 767px) 100vw, 300px"
              />
            ) : (
              <span className={css.portraitInitials}>{getInitials(displayName)}</span>
            )}
            {isTopAdvisor ? <span className={css.portraitTopBadge}>Top Advisor</span> : null}
          </div>

          <div className={css.dossierBody}>
            {showApprovedRole ? (
              <p className={css.dossierRole}>{ROLE_LABELS[category] || category}</p>
            ) : showPendingRole ? (
              <p className={css.dossierRolePending}>Expert • Pending approval</p>
            ) : null}

            <Heading as="h1" rootClassName={css.dossierName}>
              {isMaster && <VocaTalesExpertBadge size="md" />}
              {displayName ? (
                <FormattedMessage
                  id="ProfilePage.desktopHeading"
                  values={{
                    name: displayName,
                    // The translation may come from the Console-hosted asset,
                    // which wraps the name in <em> tags — the red pen goes
                    // under whatever the em chunk contains.
                    em: chunks => (
                      <span className={css.penWrap}>
                        {chunks}
                        <PenUnderline />
                      </span>
                    ),
                  }}
                />
              ) : null}
            </Heading>

            {headline ? <p className={css.dossierHeadline}>{headline}</p> : null}

            {location || price || reviewsCount > 0 ? (
              <div className={css.dossierMeta}>
                {location ? (
                  <span className={css.dossierLocation}>
                    <MapPin size={14} />
                    {location}
                  </span>
                ) : null}
                {price ? (
                  <span className={css.metaPrice}>
                    Sessions from{' '}
                    <b>
                      {CURRENCY_SYMBOLS[currency] || currency}
                      {price}
                    </b>
                  </span>
                ) : null}
                {reviewsCount > 0 ? (
                  <a href="#reviews" className={css.metaReviewsLink}>
                    <Star size={13} aria-hidden="true" />
                    <FormattedMessage
                      id="ProfilePage.reviewsLink"
                      values={{ count: reviewsCount }}
                    />
                  </a>
                ) : null}
              </div>
            ) : null}

            <div className={css.ctaRow}>
              {listingsCount > 0 ? (
                <a href="#sessions" className={css.bookButton}>
                  Book a session ↓
                </a>
              ) : null}
              {showLinkToProfileSettingsPage ? (
                <NamedLink className={css.editLink} name="ProfileSettingsPage">
                  <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                </NamedLink>
              ) : null}
              {isAdmin && !isCurrentUser && user?.id?.uuid ? (
                <ExternalLink
                  className={css.loginAsButton}
                  href={`${apiBaseUrl()}/api/initiate-login-as?user_id=${
                    user.id.uuid
                  }&target_path=/profile-settings`}
                >
                  <FormattedMessage id="ProfilePage.loginAsButton" />
                </ExternalLink>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  if (reviewsOfProvider.length === 0 && reviewsOfCustomer.length === 0) {
    return null;
  }
  return (
    <div id="reviews" className={css.mobileReviews}>
      {reviewsOfProvider.length > 0 && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsFromMyCustomersTitle"
              values={{ count: reviewsOfProvider.length }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfProvider} />
        </>
      )}

      {reviewsOfCustomer.length > 0 && (
        <>
          <H4 as="h2" className={css.mobileReviewsTitle}>
            <FormattedMessage
              id="ProfilePage.reviewsAsACustomerTitle"
              values={{ count: reviewsOfCustomer.length }}
            />
          </H4>
          <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
          <Reviews reviews={reviewsOfCustomer} />
        </>
      )}
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];
  const totalReviews = reviewsOfProvider.length + reviewsOfCustomer.length;

  return totalReviews === 0 ? null : (
    <div id="reviews" className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => {
    // Always include pastWorks field even if it has no value, and other fields with displayInProfile !== false
    return fieldConfig?.key === 'pastWorks' || fieldConfig?.showConfig?.displayInProfile !== false;
  };
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  // Filter out pastWorks field as it needs special handling
  const pastWorksField = propsForCustomFields.find(
    field => field.key === 'pastWorks' || field.key === 'pub_pastWorks'
  );

  // Always check for pastWorks directly in publicData using same method as other fields
  // Try both the original key and the prefixed version used in forms
  const pastWorksFromData =
    getFieldValue(publicData, 'pastWorks') || getFieldValue(publicData, 'pub_pastWorks');

  // Find pastWorks config from userFieldConfig to get the correct title
  const pastWorksConfig = userFieldConfig?.find(config => config.key === 'pastWorks');
  const pastWorksTitle = pastWorksConfig?.showConfig?.label || 'My Past Works';

  // Only create pastWorksData if there's actual content (not empty string or empty array)
  const hasPastWorksContent =
    pastWorksFromData &&
    // Handle string data (check if non-empty after trim)
    ((typeof pastWorksFromData === 'string' && pastWorksFromData.trim()) ||
      // Handle array data (check if non-empty array)
      (Array.isArray(pastWorksFromData) && pastWorksFromData.length > 0) ||
      // Handle object data (any other truthy object)
      (typeof pastWorksFromData === 'object' && !Array.isArray(pastWorksFromData)));

  const pastWorksData =
    pastWorksField ||
    (hasPastWorksContent
      ? {
          key: 'pastWorks',
          text: pastWorksFromData,
          title: pastWorksTitle,
          schemaType: 'text',
        }
      : null);

  // Extract website fields (URLs starting with http)
  const websiteFields = propsForCustomFields.filter(
    field =>
      field.schemaType === SCHEMA_TYPE_TEXT &&
      typeof field.text === 'string' &&
      field.text.startsWith('http')
  );

  // Filter out website fields and pastWorks from regular custom fields
  const regularCustomFields = propsForCustomFields.filter(field => {
    const isPastWorks = field.key === 'pastWorks' || field.key === 'pub_pastWorks';
    const isLocation = field.key === 'location' || field.key === 'pub_location';
    const isHeadline = field.key === 'headline' || field.key === 'pub_headline';
    const isWebsite =
      field.schemaType === SCHEMA_TYPE_TEXT &&
      typeof field.text === 'string' &&
      field.text.startsWith('http');
    return !isPastWorks && !isWebsite && !isLocation && !isHeadline;
  });

  return (
    <>
      <SectionDetailsMaybe {...props} />
      <SectionSocialMediaMaybe {...props} />
      <SectionWebsiteMaybe websiteFields={websiteFields} />
      {pastWorksData && (
        <SectionPastWorks
          pastWorks={pastWorksData.text || pastWorksData.value}
          title={pastWorksData.title}
        />
      )}
      {regularCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideoMaybe key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  // Calculate total reviews for the reviews link
  const totalReviews = reviews.length;

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div className={css.main}>
      {hasListings ? (
        <section id="sessions" className={css.listingsContainer}>
          <div className={css.bandWrap}>
            <H4 as="h2" className={css.listingsTitle}>
              <FormattedMessage
                id="ProfilePage.listingsTitle"
                values={{ count: listings.length }}
              />
            </H4>
            <ul className={css.listings}>
              {listings.map(l => (
                <li className={css.listing} key={l.id.uuid}>
                  <ListingCard listing={l} showAuthorInfo={false} />
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section className={css.readBand}>
        <div className={css.bandWrap}>
          {hasBio && (
            <Heading as="h3" className={css.bioHeading}>
              <FormattedMessage id="ProfilePage.bioHeading" />
            </Heading>
          )}

          {hasBio ? (
            <>
              <div
                className={classNames(css.bioContainer, {
                  [css.bioContainerExpanded]: bioExpanded,
                })}
              >
                <SectionTextMaybe text={bio} showAsIngress />
              </div>
              <button
                className={css.bioToggleButton}
                onClick={() => setBioExpanded(!bioExpanded)}
                aria-expanded={bioExpanded}
                aria-label={bioExpanded ? 'Show less' : 'Show more'}
              >
                {bioExpanded ? 'Show less' : 'Show more'}
                <ChevronDown
                  className={classNames(css.bioToggleIcon, {
                    [css.bioToggleIconExpanded]: bioExpanded,
                  })}
                />
              </button>
            </>
          ) : null}

          {displayName ? (
            <CustomUserFields
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFieldConfig}
              intl={intl}
            />
          ) : null}

          {hideReviews ? null : isMobileLayout ? (
            <MobileReviews
              reviews={reviews}
              queryReviewsError={queryReviewsError}
              userTypeRoles={userTypeRoles}
            />
          ) : (
            <DesktopReviews
              reviews={reviews}
              queryReviewsError={queryReviewsError}
              userTypeRoles={userTypeRoles}
            />
          )}
        </div>
      </section>
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const isAdmin = currentUser?.attributes?.profile?.protectedData?.isAdmin === true;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // Prepare OpenGraph metadata for social sharing
  // Use schema description for social sharing (don't use bio as it might be too long or poorly formatted)
  const profileDescription = intl.formatMessage(
    { id: 'ProfilePage.schemaDescription' },
    { name: displayName, marketplaceName: config.marketplaceName }
  );
  const profileImageVariants = getProfileImageVariants(profileUser);
  // Only pass image props if they exist, otherwise let Page component use defaults
  const facebookImages = profileImageVariants.facebookImages;
  const twitterImages = profileImageVariants.twitterImages;

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    const profileId = getProfileUrlIdentifier(currentUser) || currentUser?.id?.uuid;
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: profileId }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      author={displayName}
      description={profileDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageRoot}>
          <DossierHeader
            user={profileUser}
            displayName={displayName}
            metadata={metadata}
            listingsCount={rest.listings?.length || 0}
            reviewsCount={rest.reviews?.length || 0}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            isAdmin={isAdmin}
            isCurrentUser={isCurrentUser}
          />
          <div className={css.contentWrap}>
            <MainContent
              bio={bio}
              displayName={displayName}
              userShowError={userShowError}
              publicData={publicData}
              metadata={metadata}
              userFieldConfig={userFields}
              hideReviews={hasNoViewingRightsOnPrivateMarketplace}
              intl={intl}
              userTypeRoles={userTypeRoles}
              {...rest}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
